/**
 * Autoajuste del lienzo (0419 F3-UX).
 *
 * POR QUE EXISTE: un flujo de 27 nodos con 33 aristas —el Soporte Tecnico del bot replicado—
 * es ilegible si los nodos caen donde el editor los dejo. Colocarlos a mano es trabajo de
 * media hora que hay que repetir cada vez que se anade una rama.
 *
 * POR QUE NO SE USA `dagre`: no esta instalado y este grafo no lo necesita. Son arboles con
 * retorno, no grafos arbitrarios: un algoritmo por capas con ordenacion baricentrica —unas
 * setenta lineas— da un resultado igual de legible y sin dependencia nueva que mantener.
 *
 * NO TOCA NI UNA ARISTA. Solo escribe `position` en cada nodo: el ruteo del motor depende de
 * `sourceHandle`, y este fichero no lo lee siquiera.
 */

/** Tamanos asumidos. No se miden del DOM a proposito: el autoajuste debe funcionar con el
 *  lienzo cerrado o sin renderizar, p. ej. al importar un flujo. */
const ANCHO = 260;
const ALTO = 130;
const HUECO_ENTRE_CAPAS = 110;
const HUECO_ENTRE_HERMANOS = 60;

/**
 * Capa de cada nodo = su distancia mas larga desde una raiz.
 *
 * Se usa la distancia MAXIMA y no la minima para que un nodo nunca quede a la izquierda de
 * algo que lo alimenta. Con la minima, un atajo del inicio al final dibuja aristas que van
 * hacia atras y el lienzo se lee peor que sin ordenar.
 */
const calcularCapas = (nodos, aristas) => {
  const salientes = new Map();
  const entrantes = new Map();
  nodos.forEach((n) => {
    salientes.set(n.id, []);
    entrantes.set(n.id, []);
  });
  aristas.forEach((e) => {
    if (!salientes.has(e.source) || !entrantes.has(e.target)) return;
    if (e.source === e.target) return;                    // bucle sobre si mismo: no ordena nada
    salientes.get(e.source).push(e.target);
    entrantes.get(e.target).push(e.source);
  });

  const capa = new Map(nodos.map((n) => [n.id, 0]));

  // Relajacion iterativa: capa(destino) >= capa(origen) + 1. Converge en <= N pasadas y el
  // tope corta los ciclos —que en estos flujos existen: los hijos vuelven al padre—.
  const tope = nodos.length + 1;
  for (let i = 0; i < tope; i++) {
    let cambio = false;
    for (const e of aristas) {
      if (e.source === e.target) continue;
      if (!capa.has(e.source) || !capa.has(e.target)) continue;
      const propuesta = capa.get(e.source) + 1;
      if (propuesta > capa.get(e.target) && propuesta < tope) {
        capa.set(e.target, propuesta);
        cambio = true;
      }
    }
    if (!cambio) break;
  }

  return { capa, salientes, entrantes };
};

/**
 * Ordenacion dentro de cada capa por BARICENTRO: cada nodo se coloca cerca de la media de
 * sus padres. Es lo que evita que las aristas se crucen sin necesidad.
 */
const ordenarCapas = (grupos, entrantes, indiceEn) => {
  const claves = [...grupos.keys()].sort((a, b) => a - b);
  for (let pasada = 0; pasada < 4; pasada++) {
    for (const k of claves) {
      const grupo = grupos.get(k);
      const conPeso = grupo.map((id, i) => {
        const padres = (entrantes.get(id) || [])
          .map((p) => indiceEn.get(p))
          .filter((v) => v !== undefined);
        const peso = padres.length
          ? padres.reduce((a, b) => a + b, 0) / padres.length
          : i;
        return { id, peso, i };
      });
      conPeso.sort((a, b) => a.peso - b.peso || a.i - b.i);
      const ordenado = conPeso.map((x) => x.id);
      grupos.set(k, ordenado);
      ordenado.forEach((id, i) => indiceEn.set(id, i));
    }
  }
  return claves;
};

/**
 * Coloca los nodos y devuelve una copia con `position` nueva.
 *
 * @param {'vertical'|'horizontal'} direccion  vertical = las capas bajan; horizontal = avanzan
 *                                             a la derecha, que es como se leen los flujos
 *                                             grandes sin hacer scroll infinito.
 */
export const autoDisponer = (nodos, aristas, direccion = "vertical") => {
  if (!Array.isArray(nodos) || nodos.length === 0) return nodos || [];

  const { capa, entrantes } = calcularCapas(nodos, aristas || []);

  const grupos = new Map();
  nodos.forEach((n) => {
    const k = capa.get(n.id) ?? 0;
    if (!grupos.has(k)) grupos.set(k, []);
    grupos.get(k).push(n.id);
  });

  const indiceEn = new Map();
  grupos.forEach((g) => g.forEach((id, i) => indiceEn.set(id, i)));
  const claves = ordenarCapas(grupos, entrantes, indiceEn);

  // La capa mas poblada marca el ancho del lienzo; las demas se centran contra ella para que
  // el dibujo salga simetrico en vez de pegado a un borde.
  const maxEnUnaCapa = Math.max(...claves.map((k) => grupos.get(k).length));
  const horizontal = direccion === "horizontal";
  const pasoPrincipal = horizontal ? ANCHO + HUECO_ENTRE_CAPAS : ALTO + HUECO_ENTRE_CAPAS;
  const pasoHermanos = horizontal ? ALTO + HUECO_ENTRE_HERMANOS : ANCHO + HUECO_ENTRE_HERMANOS;

  const posiciones = new Map();
  claves.forEach((k, indiceCapa) => {
    const grupo = grupos.get(k);
    const desplazamiento = ((maxEnUnaCapa - grupo.length) * pasoHermanos) / 2;
    grupo.forEach((id, i) => {
      const principal = indiceCapa * pasoPrincipal;
      const cruzado = desplazamiento + i * pasoHermanos;
      posiciones.set(id, horizontal ? { x: principal, y: cruzado } : { x: cruzado, y: principal });
    });
  });

  return nodos.map((n) => ({
    ...n,
    position: posiciones.get(n.id) || n.position || { x: 0, y: 0 },
  }));
};

export default autoDisponer;
